import React, { useState, useEffect, useRef } from "react";
import type { SelectOption } from "../../interfaces/componentInterfaces/dropdown";
import "./CustomSelectDropdown.scss";

export interface CustomSelectDropdownProps {
  id: string;
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const CustomSelectDropdown: React.FC<CustomSelectDropdownProps> = ({
  id,
  options,
  value,
  onChange,
  disabled = false,
  placeholder = "Select...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: string) => {
    if (onChange && optionValue !== value) {
      onChange(optionValue);
    }
    setIsOpen(false);
  };

  const selectedOption = options.find((opt) => opt.value === value && opt.value !== "");
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  const filteredOptions = options.filter((opt) => opt.value !== ""); // Remove placeholder option

  const isPlaceholder = !selectedOption;

  return (
    <div
      className={`custom-select-dropdown ${isOpen ? "custom-select-dropdown--open" : ""} ${disabled ? "custom-select-dropdown--disabled" : ""} ${isPlaceholder ? "custom-select-dropdown--placeholder" : ""}`}
      ref={dropdownRef}
    >
      <div
        className="custom-select-dropdown__trigger"
        onClick={handleToggle}
        id={id}
      >
        <span className="custom-select-dropdown__value">{displayValue}</span>
        <i className="f-icon chevron-down-icon"></i>
      </div>

      {isOpen && (
        <div className="custom-select-dropdown__menu">
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`custom-select-dropdown__item ${value === option.value ? "custom-select-dropdown__item--selected" : ""}`}
              onClick={() => handleSelect(option.value)}
            >
              <span className="custom-select-dropdown__item-label">
                {option.label}
              </span>
              {value === option.value && (
                <i className="f-icon check-icon"></i>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelectDropdown;

